import { AlertCircle, ArrowUpRight, Target, TrendingUp, Users } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useApi } from "@/hooks/use-api";
import { formatCurrency, formatDate, formatPercent } from "@/lib/i18n";

interface TopReferrer {
  personId: string;
  name: string;
  referralCount: number;
  revenueCents: number;
}

interface ReferralSummary {
  totalDeals: number;
  referralDeals: number;
  referralPercent: number;
  referralCloseRate: number;
  nonReferralCloseRate: number;
  referralAvgLtvCents: number;
  nonReferralAvgLtvCents: number;
  ltvMultiplier: number | null;
  topReferrers: TopReferrer[];
  projectedMonthlyImpactCents: number;
  aiInsight: string;
}

interface RecentReferral {
  personId: string;
  name: string;
  referredBy: string;
  createdAt: string;
  leadScore: number | null;
}

interface ReferralAnalyticsResponse {
  summary: ReferralSummary;
  recentReferrals: RecentReferral[];
}

function isEmptyState(data: ReferralAnalyticsResponse | null) {
  if (!data) return false;

  return data.summary.referralDeals === 0
    && data.summary.topReferrers.length === 0
    && data.recentReferrals.length === 0;
}

export default function ReferralIntelligencePage() {
  const { data, isLoading, error } = useApi<ReferralAnalyticsResponse>("/crm/analytics/referrals");

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} className="h-28" />
          ))}
        </div>
        <SkeletonCard className="h-24" />
        <div className="grid gap-6 lg:grid-cols-2">
          <SkeletonCard className="h-80" />
          <SkeletonCard className="h-80" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="mr-2 inline h-4 w-4" />
          Failed to load referral intelligence. {error}
        </div>
      </div>
    );
  }

  if (isEmptyState(data)) {
    return (
      <div className="p-6">
        <EmptyState
          icon="users"
          title="No referrals yet"
          description="Referral intelligence appears once referred leads and their deal outcomes start landing in CRM."
          actionPath="/crm/contacts"
          actionLabel="Open contacts"
        />
      </div>
    );
  }

  const summary = data?.summary;
  const recentReferrals = data?.recentReferrals ?? [];
  const closeRateLift = summary ? summary.referralCloseRate - summary.nonReferralCloseRate : 0;
  const topReferrers = summary?.topReferrers ?? [];

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Referral Intelligence</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Real referral contribution, close-rate lift, top referrers, and the latest referred contacts landing in CRM.
          </p>
        </div>
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
          {summary?.totalDeals ?? 0} tracked deals
        </div>
      </div>

      {summary?.aiInsight ? (
        <div className="rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-4 text-sm text-[var(--color-muted-foreground)]">
          <div className="mb-1 flex items-center gap-2 font-medium text-[var(--color-foreground)]">
            <TrendingUp className="h-4 w-4 text-[var(--color-primary)]" />
            Referral signal
          </div>
          <p>{summary.aiInsight}</p>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={Users}
          label="Referral-sourced deals"
          value={String(summary?.referralDeals ?? 0)}
          detail={`${summary?.referralPercent ?? 0}% of total deals`}
        />
        <SummaryCard
          icon={Target}
          label="Referral close rate"
          value={formatPercent((summary?.referralCloseRate ?? 0) * 100)}
          detail={`${closeRateLift >= 0 ? "+" : ""}${Math.round(Math.abs(closeRateLift) * 100)} pts vs non-referral`}
          tone={closeRateLift >= 0 ? "success" : "warning"}
        />
        <SummaryCard
          icon={ArrowUpRight}
          label="Referral LTV"
          value={formatCurrency(summary?.referralAvgLtvCents ?? 0)}
          detail={summary?.ltvMultiplier != null
            ? `${summary.ltvMultiplier.toFixed(1)}x non-referral LTV`
            : "No non-referral benchmark yet"}
        />
        <SummaryCard
          icon={TrendingUp}
          label="Projected monthly impact"
          value={formatCurrency(summary?.projectedMonthlyImpactCents ?? 0)}
          detail="If referral asks become systematic"
          tone="primary"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="border-b border-[var(--color-border)] px-4 py-3">
            <h2 className="font-semibold text-[var(--color-foreground)]">Top referrers</h2>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Ranked by referral count, with revenue attributed from their referred deals.
            </p>
          </div>
          <div className="divide-y divide-[var(--color-border)]">
            {topReferrers.map((referrer, index) => (
              <div key={referrer.personId} className="grid gap-3 px-4 py-3 md:grid-cols-[40px_1fr_auto_auto] md:items-center">
                <div className="text-sm font-semibold text-[var(--color-muted-foreground)]">#{index + 1}</div>
                <div>
                  <div className="font-medium text-[var(--color-foreground)]">{referrer.name}</div>
                  <div className="text-sm text-[var(--color-muted-foreground)]">
                    {referrer.referralCount} referral{referrer.referralCount === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="text-sm text-[var(--color-muted-foreground)]">Revenue influenced</div>
                <div className="text-right font-medium text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatCurrency(referrer.revenueCents)}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="border-b border-[var(--color-border)] px-4 py-3">
            <h2 className="font-semibold text-[var(--color-foreground)]">Recent referral activity</h2>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              The newest contacts created through referral sources.
            </p>
          </div>
          {recentReferrals.length === 0 ? (
            <div className="px-4 py-6 text-sm text-[var(--color-muted-foreground)]">
              No recent referrals yet.
            </div>
          ) : (
            <div className="divide-y divide-[var(--color-border)]">
              {recentReferrals.map((referral) => (
                <div key={referral.personId} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-[var(--color-foreground)]">{referral.name}</div>
                      <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                        Referred by {referral.referredBy}
                      </div>
                    </div>
                    <div className="text-right text-sm text-[var(--color-muted-foreground)]">
                      <div>{formatDate(referral.createdAt)}</div>
                      <div style={{ fontVariantNumeric: "tabular-nums" }}>
                        Score {referral.leadScore ?? "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  detail,
  tone = "default",
}: {
  icon: typeof Users;
  label: string;
  value: string;
  detail: string;
  tone?: "default" | "success" | "warning" | "primary";
}) {
  const valueClass = {
    default: "text-[var(--color-foreground)]",
    success: "text-[var(--color-success)]",
    warning: "text-[var(--color-warning)]",
    primary: "text-[var(--color-primary)]",
  }[tone];

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div className={`mt-3 text-2xl font-bold ${valueClass}`} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">{detail}</div>
    </div>
  );
}
